import React from 'react';
import { View, ActivityIndicator, Text, FlatList, StyleSheet } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { strings } from '@/constants/residents/strings';
import { NotificationRow } from './NotificationRow';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';

type Props = {
  data: NotificationItem[] | undefined;
  isLoading: boolean;
  emptyText?: string;
  renderRight?: (item: NotificationItem) => React.ReactNode;
  onPressItem: (item: NotificationItem) => void;
  showDateSmall?: boolean;
  dateFor?: (item: NotificationItem) => string;
  selectedId?: number | null;
  rowCompact?: boolean;
  rowTitleStyleOverride?: any;
  rowRightWidth?: number;
  showSeparators?: boolean;
  rowVariant?: 'popover' | 'screen';
};

export const NotificationList: React.FC<Props> = ({ data, isLoading, emptyText, renderRight, onPressItem, showDateSmall, dateFor, selectedId, rowCompact, rowTitleStyleOverride, rowRightWidth, showSeparators = true, rowVariant }) => {
  const { isUnread, formatDateMMMdd, formatDateMMMddyyyy } = useNotifications();
  const keyExtractor = React.useCallback((item: NotificationItem) => String(item.id), []);
  const renderItem = React.useCallback(({ item }: { item: NotificationItem }) => (
    <NotificationRow
      item={item}
      onPress={() => onPressItem(item)}
      isSelected={selectedId === item.id}
      showDateSmall={!!showDateSmall}
      rightAccessory={renderRight ? renderRight(item) : undefined}
      unread={isUnread(item)}
      dateText={dateFor ? dateFor(item) : (showDateSmall ? formatDateMMMddyyyy(item.createdAt) : formatDateMMMdd(item.createdAt))}
      compact={rowVariant ? rowVariant === 'popover' : !!rowCompact}
      titleStyleOverride={rowVariant ? (rowVariant === 'popover' ? { fontSize: 14, fontWeight: '700', color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY } : undefined) : rowTitleStyleOverride}
      rightWidth={rowVariant ? (rowVariant === 'popover' ? 52 : undefined) : rowRightWidth}
    />
  ), [onPressItem, selectedId, isUnread, renderRight, showDateSmall, dateFor, rowVariant, rowCompact, rowTitleStyleOverride, rowRightWidth, formatDateMMMdd, formatDateMMMddyyyy]);

  if (!isLoading && Array.isArray(data) && data.length === 0) {
    return <Text style={styles.emptyText}>{emptyText || strings.notifications.desktop.empty}</Text>;
  }

  return (
    <FlatList
      data={data || []}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={showSeparators ? (() => <View style={{ height: 8 }} />) : undefined}
      contentContainerStyle={{ paddingVertical: 8 }}
      ListFooterComponent={isLoading ? (
        <ActivityIndicator style={{ marginTop: 24 }} size="large" color={RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY} />
      ) : null}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={true}
      windowSize={7}
      initialNumToRender={10}
      maxToRenderPerBatch={10}
    />
  );
};

const styles = StyleSheet.create({
  emptyText: {
    textAlign: 'center',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginTop: 48,
  },
});

export default NotificationList;


